"use client"

import React, {useState} from 'react';
import Header2 from "@/app/(index)/header2";
import cx from "clsx";
import {Noto_Sans_TC} from 'next/font/google';
import useGlobalStateStore from "@/app/(index)/use-global-state-store";
import Footer from "@/app/(index)/footer";
import Bread from "@/app/(index)/bread";
import {Button, Divider, Text, Textarea, TextInput, UnstyledButton} from "@mantine/core";
import {useForm, zodResolver} from "@mantine/form";
import {IOpinionDTO} from "@/form/opinion";
import {z} from "zod";
import {opinionSubmit} from "@/service/opinion";
import {IconCaretRight, IconSquareRoundedFilled} from "@tabler/icons-react";
import Link from "next/link";
import styles from "@/app/(index)/index.module.css";
import {useMediaQuery} from "@mantine/hooks";

const notoSansTC = Noto_Sans_TC({
    weight: ['400', '500', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});

export default function Page() {
    const { fontSize } = useGlobalStateStore();
    const [tab, setTab] = useState<number>(1);
    const [opinionDone, setOpinionDone] = useState<boolean>(false);
    const loadCurrentFontSize = (s: number, m: number, l: number) => {
        if(fontSize == 0) return `text-[${s}px]`;
        else if(fontSize == 1) return `text-[${m}px]`;
        else if(fontSize == 2) return `text-[${l}px]`;
    }

    const nonEmptyString = z.string({message: "欄位必填"}).nullable().refine(value => value !== null && value.trim() !== '', {
        message: '欄位必填',
    });

    const schema = z.object({
        name: nonEmptyString,
        mail: z.string().email({message: "EMAIL 格式錯誤"}),
        title: nonEmptyString,
        content: nonEmptyString,
        tel: nonEmptyString,
    });

    const form = useForm<IOpinionDTO>({
        mode: 'controlled',
        validate: zodResolver(schema),
        initialValues: {
            name: '',
            mail: '',
            title: '',
            content: '',
            tel: '',
        }
    });

    const handleFormSubmit = () => {
        opinionSubmit(form.values).then(() => {
            setOpinionDone(true)
        });
    }

    const mediaQueryLessSM = useMediaQuery('(min-width: 480px)');
    return (
        <div className={cx("min-h-screen flex flex-col justify-between", notoSansTC.className)}>
            <div>
                <Header2/>
                {
                    mediaQueryLessSM && (
                        <Bread name="科研資料核心研究團隊 - 帳號申請與服務說明"/>
                    )
                }
                <div
                    className={cx("mx-auto mb-[100px]", mediaQueryLessSM ? 'w-[1250px]' : 'w-full mt-[100px] px-[10px]')}>
                    <div
                        className="bg-[url('/images/title_topBG.png')] bg-[length:100%_5px] bg-no-repeat bg-top inline-block pt-[15px] text-[#5b5b5b] text-[35px] font-bold">
                        帳號申請與服務說明
                    </div>


                    <div>
                        <div className={cx("flex gap-1 border-b border-[#79b7b4]", mediaQueryLessSM ? 'justify-end' : 'mt-10')}>
                            <UnstyledButton onClick={() => setTab(1)}>
                                <div
                                    className={cx(tab === 1 ? styles.bookmarkBG1Active : styles.bookmarkBG1,
                                        "text-white py-[10px] px-[35px] font-bold", loadCurrentFontSize(14, 18, 20))}>
                                    用戶帳號申請
                                </div>
                            </UnstyledButton>
                            <UnstyledButton onClick={() => setTab(2)}>
                                <div
                                    className={cx(tab === 2 ? styles.bookmarkBG1Active : styles.bookmarkBG1,
                                        "text-white py-[10px] px-[35px] font-bold", loadCurrentFontSize(14, 18, 20))}>
                                    申請服務說明
                                </div>
                            </UnstyledButton>
                        </div>
                    </div>

                    <div className="mt-[40px]">

                        {
                            tab == 1 && (
                                <div className="flex flex-col gap-8">
                                    <div className="flex items-start gap-3">
                                        <div className="relative top-[13px]">
                                            <IconSquareRoundedFilled size={10}/>
                                        </div>
                                        <div>
                                            <Text c="blue" fw={700} size="xl">申請用戶帳號</Text>
                                            <div className="text-lg">向本中心申請各項資訊服務前，請先線上申請「團體或個人帳號」，逐項以正楷填妥送出申請，
                                                俟本中心於作業完成後，即將以E-mail通知您的帳號及密碼。為維護您的權益，請於收到通知後立即上線<Link
                                                    target="_blank"
                                                    href="https://weblib.stpi.niar.org.tw/8787/olor/owa/ismw.ismwinitactpwd.jsp"
                                                    className="text-blue-700 underline">更改密碼</Link>，以免為他人盜用。如有疑問，請電：(02)2737-7647
                                                或 E-mail：<Link target="_blank" href="mailto:stpidoc@niar.org.tw"
                                                                className="text-blue-700 underline">stpidoc@niar.org.tw</Link>。
                                            </div>

                                            <div className="flex gap-3 pt-5">
                                                <Button component="a" target="_blank"
                                                        href={"https://weblib.stpi.niar.org.tw/8787/olor/owa/ismpersecurity.Pers_security05.jsp"}>團體帳號線上申請</Button>
                                                <Button component="a" target="_blank"
                                                        href={"https://weblib.stpi.niar.org.tw/8787/olor/owa/ismpersecurity.Pers_security01.jsp"}>個人帳號線上申請</Button>
                                            </div>

                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="relative top-[13px]">
                                            <IconSquareRoundedFilled size={10}/>
                                        </div>
                                        <div>
                                            <Text c="blue" fw={700} size="xl">更動基本資料</Text>
                                            <div
                                                className="text-lg">本中心各項資訊服務之作業，都是依據您所提供的基本資料（如地址、電話、傳真、發票抬頭或e-mail等），故為維護您的權益，當您上述相關資料有所變動時，請以
                                                E-mail 或傳真等書面方式通知本中心。
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="relative top-[13px]">
                                            <IconSquareRoundedFilled size={10}/>
                                        </div>
                                        <div>
                                            <Text c="blue" fw={700} size="xl">終止用戶帳號</Text>
                                            <div className="text-lg">
                                                <ul className="list-inside list-decimal leading-8">
                                                    <li>如兩年內未曾申請各項資訊服務，本中心將自動停用您的帳號；如尚需使用本中心資訊服務，請通知服務窗口重新啟用帳號。</li>
                                                    <li>若您確定已不需本中心所提供之任何資訊服務，請以書面方式向本中心提出申請，完成申請程序並結清所有帳款後，您的帳號便已終止。</li>
                                                </ul>


                                                <div className="border-4 border-double border-gray-700 bg-white p-5 mt-5">
                                                    <div className={cx("flex gap-1", mediaQueryLessSM ? '': 'flex-col mb-5')}>
                                                        <div className="w-[100px] text-justify">郵寄地址：</div>
                                                        <div>(10636)台北市和平東路二段106號15樓</div>
                                                    </div>
                                                    <div
                                                        className={cx("flex gap-1", mediaQueryLessSM ? '' : 'flex-col mb-5')}>
                                                        <div className="w-[100px] text-justify">收件人：</div>
                                                        <div>國研院科技政策中心 科研組全文服務 收</div>
                                                    </div>
                                                    <div
                                                        className={cx("flex gap-1", mediaQueryLessSM ? '' : 'flex-col')}>
                                                        <div className="w-[100px] text-justify">傳真：</div>
                                                        <div>(02)2737-7494</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }


                        {
                            tab == 2 && (
                                <div className="flex flex-col gap-8">
                                    <div className="flex items-start gap-3">
                                        <div className="relative top-[13px]">
                                            <IconSquareRoundedFilled size={10}/>
                                        </div>
                                        <div>
                                            <Text c="blue" fw={700} size="xl">申請資訊服務</Text>
                                            <div className="text-lg">
                                                <ul className="list-inside list-decimal leading-8">
                                                    <li>全文申請：請利用本中心網站資訊服務之<Link target="_blank"
                                                                                                 href="https://weblib.stpi.niar.org.tw/8787/olor/owa/ismwsrvclient.ismwinitclientfc.jsp"
                                                                                                 className="text-blue-700 underline">全文申請表</Link>提出申請。
                                                    </li>
                                                    <li>訂購本中心出版品：請至<Link target="_blank"
                                                                                   href="https://payment.narlabs.org.tw/stpibooks/"
                                                                                   className="text-blue-700 underline">電子書城</Link>加入會員線上訂購。
                                                    </li>
                                                </ul>
                                            </div>


                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="relative top-[13px]">
                                            <IconSquareRoundedFilled size={10}/>
                                        </div>
                                        <div>
                                            <Text c="blue" fw={700} size="xl">查詢申請件處理情形</Text>
                                            <div className="text-lg">若您想瞭解在本中心申請資訊服務的情形，可逕點選<Link
                                                target="_blank"
                                                href="https://weblib.stpi.niar.org.tw/8787/olor/owa/ismwrec.ismwinitrec.jsp"
                                                className="text-blue-700 underline">帳務及服務紀錄查詢</Link>上線查詢，或以電話向本中心服務人員詢問。
                                            </div>

                                            <div className="bg-white pt-5 text-pink-600 font-bold text-lg">
                                                <div className="flex gap-1">
                                                    <div className="w-[140px] text-justify">全文提供服務：</div>
                                                    <div>(02)2737-7910</div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <div className="w-[140px] text-justify">出版品服務：</div>
                                                    <div>(02)2737-7647</div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <div className="w-[140px] text-justify">帳務查詢：</div>
                                                    <div>(02)2737-7647</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="relative top-[13px]">
                                            <IconSquareRoundedFilled size={10}/>
                                        </div>
                                        <div>
                                            <Text c="blue" fw={700} size="xl">繳費方式</Text>
                                            <div className="text-lg">
                                                <div>請於收到發票後，登入「線上查詢服務紀錄」點選「線上繳費系統」勾選發票號碼進行繳費。</div>
                                                <div>目前提供以下付費方式：</div>
                                                <div>一、信用卡付費</div>
                                                <div>二、ATM / WebATM即時繳款。</div>

                                                <ul className="list-inside list-decimal leading-8 pt-5">
                                                    <li>所有應繳金額應於訂單成立起六個月內繳清。</li>
                                                    <li>請務必於收到發票後再線上繳費，否則付款資料尚未匯入，無法進行。</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="relative top-[13px]">
                                            <IconSquareRoundedFilled size={10}/>
                                        </div>
                                        <div>
                                            <Text c="blue" fw={700} size="xl">溢付退款</Text>
                                            <div className="text-lg">
                                                <div>如果您在本中心有預付款並要退回，請以傳真或信函等方式，註明帳戶名稱、帳號、退款金額、日期等資料，簽章後擲回本中心辦理，作業時間約兩星期。</div>
                                                <div>附註：自95年元月1日起停止預付款服務，若有原預付金額請依以上方式申請退款。</div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            )
                        }

                    </div>
                </div>
            </div>
            <Footer/>
        </div>
    )
        ;
}
